import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import {
  addTicketParticipants,
  getParticipantCandidates,
  getTicketParticipants,
  removeTicketParticipants,
  type ParticipantCandidate,
  type TicketParticipantsResponse,
} from "../request/request";
import { describeParticipantsError } from "../utils/participants";
import { DetailField } from "./ticketDetails/DetailField";
import { ParticipantPicker } from "./ParticipantPicker";

type Props = {
  ticketId: string | number;
};

/**
 * Request participants of a Kontrol Edge ticket (CTX-683).
 *
 * Whoever is added sees the ticket in their own list and gets its emails;
 * the rest of their customer does not. Who may add whom is decided by the
 * backend, which also hands over the customers the picker may offer — this
 * block never works out the tenant tree on its own.
 */
export default function TicketParticipants({ ticketId }: Props) {
  const api = useAxiosAuth0();
  const [state, setState] = useState<TicketParticipantsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isAdding, setIsAdding] = useState(false);
  const [selected, setSelected] = useState<ParticipantCandidate[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTicketParticipants(api, ticketId)
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch((err) => {
        if (!cancelled) setError(describeParticipantsError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, ticketId]);

  const candidateTenants = useMemo(
    () => state?.candidateTenants ?? [],
    [state]
  );
  const loadCandidates = useCallback(
    (tenantId: number) => getParticipantCandidates(api, ticketId, tenantId),
    [api, ticketId]
  );

  const resetPicker = useCallback(() => {
    setIsAdding(false);
    setSelected([]);
  }, []);

  const replaceParticipants = (
    participants: TicketParticipantsResponse["participants"]
  ) => setState((prev) => (prev ? { ...prev, participants } : prev));

  const handleAdd = async () => {
    const emails = selected.map((c) => c.email);
    if (!emails.length || saving) return;
    setSaving(true);
    setError(null);
    try {
      replaceParticipants(await addTicketParticipants(api, ticketId, emails));
      resetPicker();
    } catch (err) {
      setError(describeParticipantsError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (accountId: string) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      replaceParticipants(
        await removeTicketParticipants(api, ticketId, [accountId])
      );
    } catch (err) {
      setError(describeParticipantsError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DetailField label="Participants">
        <SkeletonLine w="160px" h={18} />
      </DetailField>
    );
  }

  const participants = state?.participants ?? [];
  const canManage = Boolean(state?.canManage);
  // Lives in the narrow side column of the ticket detail (CTX-1121), so the
  // picker stacks vertically. With nobody to show, the field stays empty.
  return (
    <DetailField label="Participants">
      <div className="flex flex-col gap-2">
        {participants.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {participants.map((p) => (
              <GenericTag
                key={p.accountId}
                label={p.displayName || p.emailAddress || p.accountId}
                tooltip={p.emailAddress ?? undefined}
                variant="neutral"
                size="small"
                removable={canManage}
                disabled={saving}
                onRemove={() => handleRemove(p.accountId)}
              />
            ))}
          </div>
        )}

        {canManage && !isAdding && (
          <div>
            <GenericButton
              variant="secondary"
              size="sm"
              label="Add participants"
              onClick={() => setIsAdding(true)}
            />
          </div>
        )}

        {isAdding && (
          <div className="flex flex-col gap-3">
            <ParticipantPicker
              candidateTenants={candidateTenants}
              loadCandidates={loadCandidates}
              selected={selected}
              onChange={setSelected}
              disabled={saving}
              emptyItemsMessage="Everyone in this customer already follows the ticket"
            />
            <div className="flex justify-end gap-2">
              <GenericButton
                variant="secondary"
                size="sm"
                label="Cancel"
                onClick={resetPicker}
                disabled={saving}
              />
              <GenericButton
                variant="brand"
                size="sm"
                label={selected.length > 1 ? `Add (${selected.length})` : "Add"}
                onClick={handleAdd}
                disabled={!selected.length}
                loading={saving}
              />
            </div>
          </div>
        )}

        {error && <p className="text-xs text-semantic-text-danger">{error}</p>}
      </div>
    </DetailField>
  );
}
